import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../supabase';
import DirectoryContactDetail from './DirectoryContactDetail';
import DirectoryContactModal from './DirectoryContactModal';
import {
  avatarStyle, statusStyle, statusLabel, initials, isDormant,
  INTRODUCED_BY_SHORT, fmtDateShort, daysSince,
} from '../directory';

const HAMIT_SOURCES = ['hamit_turkish_network', 'hamit_existing_customer_outreach'];

const BADGE = {
  display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 600,
  padding: '3px 9px', borderRadius: 20, letterSpacing: '0.02em', whiteSpace: 'nowrap',
};

function matchesFilter(c, filter) {
  switch (filter) {
    case 'network':    return c.contact_kind === 'network';
    case 'supplier':   return c.contact_kind === 'supplier';
    case 'hamit':      return HAMIT_SOURCES.includes(c.introduced_by);
    case 'discussing': return c.status === 'discussing_work';
    case 'dormant':    return isDormant(c);
    default:           return true;
  }
}

function sortContacts(a, b) {
  const ad = a.next_action_date, bd = b.next_action_date;
  if (ad && bd) { if (ad !== bd) return ad < bd ? -1 : 1; }
  else if (ad && !bd) return -1;
  else if (!ad && bd) return 1;
  return (b.updated_at || '').localeCompare(a.updated_at || '');
}

function Chip({ active, label, count, onClick }) {
  return (
    <button onClick={onClick} className="btn-ghost" style={{
      padding: '6px 12px', fontSize: 12, fontWeight: 500,
      background: active ? 'var(--bg3)' : 'transparent',
      color: active ? 'var(--text)' : 'var(--text2)',
      border: `1px solid ${active ? 'var(--border2)' : 'var(--border)'}`,
    }}>
      {label}
      {count != null && (
        <span style={{
          marginLeft: 6, fontSize: 10, background: active ? 'var(--text2)' : 'var(--border2)',
          color: active ? 'var(--bg2)' : 'var(--text2)', padding: '1px 6px', borderRadius: 8,
        }}>{count}</span>
      )}
    </button>
  );
}

function DirectoryCard({ contact, onOpen, onClose, canEdit }) {
  const dormant = isDormant(contact);
  const closed = contact.status === 'closed';
  const faded = dormant || closed;
  const av = avatarStyle(contact);
  const st = statusStyle(contact);

  const introShort = INTRODUCED_BY_SHORT[contact.introduced_by] || '';
  const subtitle = [
    contact.profession,
    contact.city_area,
    [introShort, fmtDateShort(contact.first_met_date)].filter(Boolean).join(' '),
  ].filter(Boolean).join(' · ');

  return (
    <div onClick={() => onOpen(contact)} style={{
      display: 'flex', alignItems: 'center', gap: 14,
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius2)', padding: '14px 16px', marginBottom: 8,
      cursor: 'pointer', opacity: faded ? 0.65 : 1, transition: 'border-color 0.15s',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
        background: av.bg, color: av.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, fontWeight: 700,
      }}>{initials(contact.full_name)}</div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 14, fontWeight: 700, color: 'var(--text)',
        }}>
          <span style={{
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{contact.full_name}</span>
          {contact.source && (
            <span title={`${contact.source} ekledi`} style={{ fontSize: 11, flexShrink: 0 }}>🤖</span>
          )}
        </div>
        <div style={{
          fontSize: 12, color: 'var(--text3)', marginTop: 2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{subtitle}</div>
      </div>

      <div style={{ flexShrink: 0, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        {dormant ? (
          <span style={{ ...BADGE, color: 'var(--text3)', background: 'var(--bg3)' }}>Sessiz · 60+ gün</span>
        ) : !closed && (
          <span style={{ ...BADGE, color: st.color, background: st.bg }}>{statusLabel(contact)}</span>
        )}
        {dormant ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={e => { e.stopPropagation(); onOpen(contact); }} style={{
              background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11, padding: 0, cursor: 'pointer',
            }}>Canlandır</button>
            {canEdit && (
              <button onClick={e => { e.stopPropagation(); onClose(contact); }} style={{
                background: 'none', border: 'none', color: 'var(--text3)', fontSize: 11, padding: 0, cursor: 'pointer',
              }}>Kapat</button>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            {contact.next_action_date ? `Sıradaki: ${fmtDateShort(contact.next_action_date)}` : 'Sıradaki yok'}
          </div>
        )}
      </div>
    </div>
  );
}

export async function fetchDirectoryContacts(){
 const {data,error}=await supabase.from('directory_contacts').select('*').order('updated_at',{ascending:false});
 if(error)throw new Error(error.message);
 if(!Array.isArray(data))throw new Error('Sunucudan geçerli kayıt listesi alınamadı.');
 return data;
}

export default function DirectoryPage({ user, isAdmin, isPipeline, customers = [] }) {
  const canEdit = isAdmin || isPipeline;
  const canDelete = isAdmin;

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError,setLoadError]=useState('');
  const [view, setView] = useState('list');
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // null | { mode, contact }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data=await fetchDirectoryContacts();
      setContacts(data);setLoadError('');
    } catch(e) {
      setLoadError('Rehber yüklenemedi. Kayıtların silindiği anlamına gelmez. '+e.message);
    } finally {setLoading(false);}
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    all: contacts.length,
    network: contacts.filter(c => c.contact_kind === 'network').length,
    supplier: contacts.filter(c => c.contact_kind === 'supplier').length,
    hamit: contacts.filter(c => HAMIT_SOURCES.includes(c.introduced_by)).length,
    discussing: contacts.filter(c => c.status === 'discussing_work').length,
    dormant: contacts.filter(c => isDormant(c)).length,
  }), [contacts]);

  const thisWeek = useMemo(
    () => contacts.filter(c => { const d = daysSince(c.created_at); return d != null && d < 7; }).length,
    [contacts],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts
      .filter(c => matchesFilter(c, filter))
      .filter(c => {
        if (!q) return true;
        const hay = [
          c.full_name, c.profession, c.company, c.city_area, c.intro_detail, c.typical_sentence,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      })
      .sort(sortContacts);
  }, [contacts, filter, search]);

  const selectedContact = contacts.find(c => c.id === selectedId) || null;

  const openContact = (c) => { setSelectedId(c.id); setView('detail'); };
  const backToList = () => { setView('list'); setSelectedId(null); };

  const handleSaved = (saved, { created }) => {
    setContacts(prev => (created ? [saved, ...prev] : prev.map(c => (c.id === saved.id ? saved : c))));
    setModal(null);
  };

  const handleContactUpdated = (updated) => {
    setContacts(prev => prev.map(c => (c.id === updated.id ? updated : c)));
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`${c.full_name} kaydını ve tüm notlarını silmek istediğine emin misin?`)) return;
    const { error } = await supabase.from('directory_contacts').delete().eq('id', c.id);
    if (error) { alert('Silinemedi: ' + error.message); return; }
    setContacts(prev => prev.filter(x => x.id !== c.id));
    backToList();
  };

  const closeContact = async (c) => {
    if (!window.confirm(`${c.full_name} ilişkisini kapatmak istediğine emin misin?`)) return;
    const { data, error } = await supabase
      .from('directory_contacts')
      .update({ status: 'closed', updated_at: new Date().toISOString() })
      .eq('id', c.id)
      .select()
      .single();
    if (error) { alert('Güncellenemedi: ' + error.message); return; }
    setContacts(prev => prev.map(x => (x.id === c.id ? data : x)));
  };

  if (view === 'detail' && selectedContact) {
    return (
      <>
        <DirectoryContactDetail
          contact={selectedContact}
          user={user}
          canEdit={canEdit}
          canDelete={canDelete}
          onBack={backToList}
          onEdit={(c) => setModal({ mode: 'edit', contact: c })}
          onDelete={handleDelete}
          onContactUpdated={handleContactUpdated}
        />
        {modal && (
          <DirectoryContactModal
            contact={modal.mode === 'edit' ? modal.contact : null}
            customers={customers}
            user={user}
            onClose={() => setModal(null)}
            onSaved={handleSaved}
          />
        )}
      </>
    );
  }

  const CHIPS = [
    { id: 'all', label: 'Tümü' },
    { id: 'network', label: 'Network' },
    { id: 'supplier', label: 'Taşeron' },
    { id: 'hamit', label: 'Hamit tanıttı' },
    { id: 'discussing', label: 'İş konuşuyoruz' },
    { id: 'dormant', label: 'Sessiz' },
  ];

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h1 style={{ fontFamily: 'var(--font)', fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
              Rehber
            </h1>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>
              {loading?'Yükleniyor…':loadError&&!contacts.length?'Kayıt sayısı alınamadı':`${contacts.length} kişi`}
              {thisWeek > 0 && ` · ${thisWeek} tanesi bu hafta eklendi`}
            </div>
          </div>
          {canEdit && (
            <button className="btn-primary" onClick={() => setModal({ mode: 'new' })}>+ Yeni kişi</button>
          )}
        </div>

        <button className="btn-ghost" onClick={load} disabled={loading} style={{marginBottom:12}}>Yenile</button>
        {loadError&&<p role="alert" className="ops-error">{loadError} <button className="btn-ghost" onClick={load} disabled={loading}>Tekrar dene</button></p>}
        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
          {CHIPS.map(ch => (
            <Chip key={ch.id} label={ch.label} count={counts[ch.id]}
              active={filter === ch.id} onClick={() => setFilter(ch.id)} />
          ))}
          <input
            type="text" placeholder="Ara — isim, meslek, firma, bölge…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: '1 1 200px', minWidth: 180 }}
          />
        </div>

        {/* List */}
        {loading ? (
          <div style={{ color: 'var(--text3)', fontSize: 13 }}>Yükleniyor…</div>
        ) : loadError && contacts.length===0 ? null : contacts.length === 0 ? (
          <div className="empty-state" style={{
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius2)',
          }}>
            <div className="icon">📇</div>
            <p>Rehber boş</p>
            <span>{canEdit ? 'İlk kişiyi eklemek için "Yeni kişi"ye bas.' : 'Henüz kayıt yok.'}</span>
          </div>
        ) : visible.length === 0 ? (
          <div style={{
            color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: 40,
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius2)',
          }}>
            Filtreye veya aramaya uyan kişi yok. <button className="btn-ghost" onClick={()=>{setFilter('all');setSearch('');}}>Filtreleri temizle</button>
          </div>
        ) : (
          visible.map(c => (
            <DirectoryCard key={c.id} contact={c} canEdit={canEdit}
              onOpen={openContact} onClose={closeContact} />
          ))
        )}
      </div>

      {modal && (
        <DirectoryContactModal
          contact={modal.mode === 'edit' ? modal.contact : null}
          customers={customers}
          user={user}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
